import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { ModalProps } from '@mui/base/Modal';
import { Modal } from '@/components';
import {
  IPoiRequest,
  PoiRequestUpdateData,
} from '@/types/poi-request-management.types';
import { useGetPOICategoriesQuery } from '@/services/POIManagementService';

export interface PoiRequestEditModalProps extends Omit<
  ModalProps,
  'children' | 'onSubmit'
> {
  poiRequest: IPoiRequest | null;
  formData: PoiRequestUpdateData;
  setFormData: React.Dispatch<React.SetStateAction<PoiRequestUpdateData>>;
  errors: Record<string, string>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  onSubmit: (data: PoiRequestUpdateData) => Promise<void>;
  isSubmitting?: boolean;
}

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'Error loading categories';
};

const PoiRequestEditModal = ({
  open,
  onClose,
  poiRequest,
  formData,
  setFormData,
  errors,
  setErrors,
  onSubmit,
  isSubmitting,
  className,
}: PoiRequestEditModalProps) => {
  // Fetch categories dynamically
  const {
    data: categoriesResponse,
    isLoading: isCategoriesLoading,
    error: categoriesError,
  } = useGetPOICategoriesQuery();

  // Use local state to track form data
  const [localFormData, setLocalFormData] =
    useState<PoiRequestUpdateData>(formData);

  const errorMessage = useMemo(() => {
    return categoriesError ? getErrorMessage(categoriesError) : null;
  }, [categoriesError]);

  useEffect(() => {
    if (poiRequest && open) {
      // Use categoryId directly if available, otherwise try to find by category name
      const categoryId =
        poiRequest.categoryId ||
        categoriesResponse?.find((cat) => cat.name === poiRequest.category)
          ?.id ||
        '';

      const initialData = {
        name: poiRequest.name || '',
        category_id: categoryId,
        description: poiRequest.description || '',
      };
      setLocalFormData(initialData);
      setFormData(initialData);
      setErrors({});
    }
  }, [poiRequest, open, setFormData, setErrors, categoriesResponse]);

  const handleChange = useCallback(
    (field: keyof PoiRequestUpdateData, value: string | number | string[]) => {
      setLocalFormData((prev) => ({ ...prev, [field]: value }));
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: '' }));
      }
    },
    [errors, setFormData, setErrors]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Ensure parent formData is updated with latest localFormData before submission
    setFormData(localFormData);
    await onSubmit(localFormData);
  };

  const handleClose = (
    event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (!isSubmitting && onClose) {
      onClose(event, reason);
    }
  };

  const handleButtonClick = () => {
    if (!isSubmitting && onClose) {
      onClose({}, 'backdropClick');
    }
  };

  if (!open) return null;

  return (
    <Modal open={open} onClose={handleClose} className={className}>
      <div className="modal-content max-w-[600px] top-[5%]">
        <div className="modal-header">
          <h3 className="modal-title">Edit POI Request</h3>
          <button
            className="btn btn-xs btn-icon btn-light"
            onClick={handleButtonClick}
            disabled={isSubmitting}
            aria-label="Close modal"
          >
            <i className="ki-outline ki-cross text-2xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="grid gap-4">
              {/* Name Field */}
              <div className="flex flex-col gap-2">
                <span className="font-semibold text-gray-900">
                  Name <span className="text-danger">*</span>
                </span>

                <input
                  type="text"
                  className={`input ${errors.name ? 'input-error' : ''}`}
                  placeholder="Enter POI request name"
                  value={localFormData.name || ''}
                  onChange={(e) => handleChange('name', e.target.value)}
                  disabled={isSubmitting}
                  autoComplete="off"
                />
                {errors.name && (
                  <span className="text-danger contrast:text-red-500 text-xs">
                    {errors.name}
                  </span>
                )}
              </div>

              {/* Category Field */}
              <div className="flex flex-col gap-2">
                <span
                  id="poi-request-category-label"
                  className="font-semibold text-gray-900"
                >
                  Category <span className="text-danger">*</span>
                </span>
                <select
                  id="poi-request-category"
                  className={`select ${errors.category_id ? 'select-error' : ''}`}
                  aria-labelledby="poi-request-category-label"
                  value={localFormData.category_id || ''}
                  onChange={(e) => handleChange('category_id', e.target.value)}
                  disabled={isSubmitting || isCategoriesLoading}
                >
                  <option value="">Select a category</option>
                  {categoriesResponse && Array.isArray(categoriesResponse) ? (
                    categoriesResponse
                      .filter(
                        (category) => category && category.id && category.name
                      )
                      .map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.label || category.name}
                        </option>
                      ))
                  ) : (
                    <option value="">No categories available</option>
                  )}
                </select>
                {errors.category_id && (
                  <span className="text-danger contrast:text-red-500 text-xs">
                    {errors.category_id}
                  </span>
                )}
                {errorMessage && (
                  <span className="text-danger contrast:text-red-500 text-xs">
                    {errorMessage}
                  </span>
                )}
                {isCategoriesLoading && (
                  <span className="text-muted text-xs">
                    Loading categories...
                  </span>
                )}
              </div>

              {/* Description Field */}
              <div className="flex flex-col gap-2">
                <span className="font-semibold text-gray-900">Description</span>
                <textarea
                  className={`textarea ${errors.description ? 'textarea-error' : ''}`}
                  placeholder="Enter description"
                  rows={4}
                  value={localFormData.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                  disabled={isSubmitting}
                />
                {errors.description && (
                  <span className="text-danger contrast:text-red-500 text-xs">
                    {errors.description}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="modal-footer justify-end gap-2">
            <button
              type="button"
              className="btn btn-light"
              onClick={handleButtonClick}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Updating...
                </span>
              ) : (
                'Update POI Request'
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export { PoiRequestEditModal };
